<div data-testid="pos-profile-page" class="p-6 flex-table-page">
  <!-- Header -->
  <h2 class="text-2xl font-semibold text-gray-800 mb-3 flex-table-header">
    {{ "pos.management.title" | translate }}
  </h2>

  <app-search-form
    data-testid="pos-profile-search-form"
    [searchFormat]="searchConfig"
    (formSearch)="onSearch($event)"
    (formReset)="onReset()"
  >
    <div slot="actions" class="flex gap-2">
      <app-button
        [testId]="'pos-profile-column-config-btn'"
        type="success"
        [outlined]="true"
        icon="view_column"
        (emitOnClick)="onColumnConfigClick($event)"
      ></app-button>
      <app-status-filter
        [primaryButton]="primaryButton()"
        (primaryButtonClick)="onAdd()"
      ></app-status-filter>
    </div>
  </app-search-form>

  <!-- Data Table -->
  <div class="flex-table-section">
    <advanced-data-table
      data-testid="pos-profile-table"
      #advancedTable
      [dataSource]="posProfile()"
      [defaultColumns]="defaultColumns()"
      [flexibleColumns]="flexibleColumns()"
      [loading]="loading()"
      [totalRecords]="totalRecords()"
      [rows]="searchParams().size"
      [first]="(searchParams().page - 1) * searchParams().size"
      [hasCheckColumn]="false"
      [selection]="selectedPosProfile()"
      scrollHeight="flex"
      (selectionChange)="onSelectionChange($event)"
      (pageChange)="onPageChange($event)"
      [showInternalColumnConfigButton]="false"
    >
    <!-- Custom Status Template -->
    <ng-template #statusTemplate let-status>
      <div class="flex items-center justify-center gap-1">
        <p-tag
          data-testid="pos-profile-status-tag"
          [value]="getStatusDisplayText(status)"
          [severity]="getStatusSeverity(status)"
          styleClass="status-tag"
          [ngStyle]="getStatusStyle(status)"
          [pTooltip]="
            status
              ? ('common.table.status.active' | translate)
              : ('common.table.status.inactive' | translate)
          "
          tooltipPosition="top"
          tooltipStyleClass="custom-tooltip"
          [showDelay]="300"
          [hideDelay]="0"
        />
      </div>
    </ng-template>

    <!-- Custom Template for different column types -->
    <ng-template
      #customTemplate
      let-value
      let-column="column"
      let-rowData="rowData"
    >
      @if (column.template === "dateTemplate") {
        <span class="text-xs text-gray-600">
          {{ formatDateTime(value) }}
        </span>
      }
    </ng-template>

    <!-- Custom Actions Template -->
    <ng-template #actionsTemplate let-rowData>
      <div class="flex gap-2 justify-center">
        @if (canUpdate()) {
          <app-button
            [testId]="'pos-profile-action-generate'"
            class="action-generate"
            [label]="'common.actions.generate_activation_code' | translate"
            type="action"
            [disabled]="rowData.channelShopType !== 'OFFLINE'"
            (emitOnClick)="onGenerate(rowData)"
          ></app-button>
        }
        @if (canUpdate()) {
          <app-button
            [testId]="'pos-profile-action-edit'"
            class="action-edit"
            [label]="'common.actions.edit' | translate"
            type="action"
            [disabled]="rowData.status"
            (emitOnClick)="onEdit(rowData)"
          ></app-button>
        }
        @if (canApprove()) {
          <app-button
            [testId]="'pos-profile-action-toggle-status'"
            [class]="rowData.status ? 'action-disable' : 'action-enable'"
            [label]="
              rowData.status
                ? ('common.table.actions.deactivate' | translate)
                : ('common.table.actions.activate' | translate)
            "
            type="action"
            (emitOnClick)="onStatusChange(rowData, !rowData.status)"
          ></app-button>
        }
        @if (canRead()) {
          <app-button
            [testId]="'pos-profile-action-view'"
            class="action-view"
            [label]="'common.actions.view' | translate"
            type="action"
            (emitOnClick)="onView(rowData)"
          ></app-button>
        }
        @if (canDelete()) {
          <app-button
            [testId]="'pos-profile-action-delete'"
            class="action-delete"
            [label]="'common.actions.delete' | translate"
            type="action"
            [disabled]="rowData.status"
            [tooltip]="
              rowData.status
                ? ('pos.messages.cannot_delete_enabled' | translate)
                : ''
            "
            tooltipPosition="top"
            [showDelay]="300"
            (emitOnClick)="onDelete(rowData)"
          ></app-button>
        }
      </div>
    </ng-template>
    </advanced-data-table>
  </div>

  <!-- Toast Messages -->
  <p-toast data-testid="pos-profile-toast"></p-toast>

  <!-- Custom Confirmation Dialog -->
  <app-confirmation-dialog
    [testId]="'pos-profile-confirmation-dialog'"
    [(visible)]="showConfirmationDialog"
    [title]="confirmationTitle()"
    [message]="confirmationMessage()"
    [buttons]="confirmationButtons()"
    (emitOnClick)="onConfirmationDialogAction($event)"
  ></app-confirmation-dialog>

  <!-- Activation Code Dialog -->
  <p-dialog
    data-testid="pos-profile-activation-code-dialog"
    [(visible)]="showActivationCodeDialog"
    [modal]="true"
    [closable]="true"
    [dismissableMask]="false"
    [closeOnEscape]="false"
    [style]="{ width: '500px' }"
    [draggable]="false"
    [resizable]="false"
    (onHide)="onCloseActivationCodeDialog()"
  >
    <ng-template pTemplate="header">
      <div class="font-semibold text-lg">
        {{ "pos.dialog.activation_code_title" | translate }}
      </div>
    </ng-template>

    <div class="flex flex-col gap-4">
      <!-- Warning Message -->
      <div
        data-testid="pos-profile-activation-code-warning"
        class="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-md flex items-start gap-3"
      >
        <i
          class="pi pi-exclamation-triangle text-yellow-600 text-xl mt-0.5"
        ></i>
        <div class="flex-1">
          <p class="text-yellow-800 font-medium mb-1">
            {{ "pos.messages.activation_code_warning_title" | translate }}
          </p>
          <p class="text-yellow-700 text-sm">
            {{ "pos.messages.activation_code_warning" | translate }}
          </p>
        </div>
      </div>

      <!-- Activation Code Display -->
      <div class="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <span class="text-sm font-medium text-gray-700 mb-2 block">
          {{ "pos.labels.activation_code" | translate }}
        </span>
        <div class="flex items-center gap-2">
          <div
            data-testid="pos-profile-activation-code-value"
            class="flex-1 bg-white border border-gray-300 rounded px-4 py-3 font-mono text-lg font-bold text-blue-600 tracking-wider select-all"
          >
            {{ activationCode() }}
          </div>
          <button
            data-testid="pos-profile-copy-code-btn"
            pButton
            type="button"
            icon="pi pi-copy"
            class="p-button-outlined p-button-info"
            [pTooltip]="'pos.actions.copy_code' | translate"
            tooltipPosition="top"
            (click)="onCopyActivationCode()"
          ></button>
        </div>
      </div>
    </div>

    <ng-template pTemplate="footer">
      <button
        data-testid="pos-profile-close-dialog-btn"
        pButton
        type="button"
        [label]="'common.actions.close' | translate"
        class="p-button-info"
        (click)="onCloseActivationCodeDialog()"
      ></button>
    </ng-template>
  </p-dialog>
</div>
